import { Tree } from 'antd';
import React, { ReactText, useCallback, useContext, useEffect, useMemo } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { RootState } from '../../../../rootReducer';
import { AppDispatch } from '../../../../store';
import { getImageAnnotations, imageSelected, setIsPhotocardSelected, onNodeChange, setStatusTable } from '../annotation.slice';
import { imageUnloaded, setSaveFinding, generatePickUnselected } from '../../Canvas/canvas.slice';
import UseImagesHelpers from './Hooks/UseImagesHelpers';
import { TABLE_STATUS } from '../../../../shared/Helpers';
import { ParserContext } from '../../contexts/ParserContext';
import { isStringNullOrEmpty } from '../AnnotationForm/Utils';
import { AnnotationManagerPrefix } from '../Helpers';
import { prepareTreeStructure } from './treeUtils';
type DataNode = any;

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const ImagesTreeView = ({
  history,
  match,
  selectedSequenceInfo,
  selectedName,
  imageLoading,
  showCreateAnnotationModal,
  validationModalData,
  standardLoaded,
  presentAnnotations,
  saveFinding,
  isPhotocardSelected,
  tableStatus,
  pathContext,
  onSelectImage,
  onGetImageAnnotations,
  onSaveFinding,
  onSetIsPhotocardSelected,
  onNodeChanged,
  onImageUnloaded,
  onSetStatusTable,
  onClearToolbar
}: RouteComponentProps<any> & Props) => {
  const { parser } = useContext(ParserContext);
  const images = useMemo(() => selectedSequenceInfo && selectedSequenceInfo.images.filter(x => !x.isHidden), [selectedSequenceInfo]);
  const sequencePath = useMemo(() => parser.parsedSequencePath(match.params), [match.params, parser]);

  const navigateToImage = useCallback(
    (name: string) => {
      history.push(`/${AnnotationManagerPrefix}/${parser.name}/${parser.parsedSequencePath(match.params)}/${name}`);

      if (tableStatus === TABLE_STATUS.EXPANDED) {
        onSetStatusTable(TABLE_STATUS.NORMAL);
      }
    },
    [history, match.params, onSetStatusTable, parser, tableStatus]
  );

  const { selectImage, onListImageSelected } = UseImagesHelpers(
    sequencePath,
    selectedName,
    imageLoading,
    images,
    showCreateAnnotationModal,
    validationModalData,
    presentAnnotations,
    saveFinding,
    pathContext,
    onSelectImage,
    onGetImageAnnotations,
    onSaveFinding,
    onSetIsPhotocardSelected,
    navigateToImage,
    onClearToolbar
  );

  useEffect(() => {
    const urlImageName = parser.getImageName(match.params);

    if (isStringNullOrEmpty(urlImageName)) {
      onNodeChanged();
      onImageUnloaded();
      return;
    }

    if (urlImageName === selectedName || isStringNullOrEmpty(sequencePath) || !standardLoaded || !selectedSequenceInfo) {
      return;
    }

    selectImage(urlImageName!, isPhotocardSelected);
  }, [
    isPhotocardSelected,
    match.params,
    onImageUnloaded,
    onNodeChanged,
    parser,
    selectImage,
    selectedName,
    selectedSequenceInfo,
    sequencePath,
    standardLoaded
  ]);

  const onSelect = (
    selectedKeys: ReactText[],
    info: {
      event: 'select';
      selected: boolean;
      node: any;
      selectedNodes: DataNode[];
      nativeEvent: MouseEvent;
    }
  ) => {
    if (!(selectedKeys && selectedKeys.length)) {
      return;
    }

    const selectedKey = selectedKeys[0] as string;

    const image = images?.find(x => x.filename === selectedKey);
    if (image) {
      onListImageSelected(selectedKey, image.isPhotocard);
    }
  };

  if (!images) return null;

  const treeStructure: DataNode[] = prepareTreeStructure(images);

  return (
    <div style={{ overflowX: 'hidden', height: 'calc(100vh - 130px)' }}>
      <Tree selectedKeys={[selectedName]} defaultExpandAll={true} disabled={imageLoading} showLine treeData={treeStructure} onSelect={onSelect} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  rawImages: state.annotation.images,
  selectedSequenceInfo: state.annotation.selectedSequenceInfo,
  selectedName: state.annotation.selectedName,
  imageLoading: state.canvas.loading,
  showCreateAnnotationModal: state.annotationForm.showCreateAnnotationModal,
  validationModalData: state.annotationValidationModal.validationModalData,
  standardLoaded: !!state.annotation.annotationStandardDetails,
  presentAnnotations: state.annotation.presentAnnotations,
  saveFinding: state.canvas.saveFinding,
  isPhotocardSelected: state.annotation.isPhotocardSelected,
  tableStatus: state.annotation.tableStatus,
  pathContext: state.annotation.pathContext
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onSelectImage: (url: string, name: string) => dispatch(imageSelected(url, name)),
  onGetImageAnnotations: (isPhotocard: boolean) => dispatch(getImageAnnotations(isPhotocard)),
  onSaveFinding: (saveFinding: boolean) => dispatch(setSaveFinding(saveFinding)),
  onSetIsPhotocardSelected: (isSelected: boolean) => dispatch(setIsPhotocardSelected(isSelected)),
  onNodeChanged: () => dispatch(onNodeChange()),
  onImageUnloaded: () => dispatch(imageUnloaded()),
  onSetStatusTable: (expandHide: number) => dispatch(setStatusTable(expandHide)),
  onClearToolbar: () => dispatch(generatePickUnselected(true))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ImagesTreeView));
